/**
 * Catalog service — public businesses and categories.
 * Falls back to demo data when API fails (e.g. missing business_categories table).
 * Demo: at least 3 businesses per category, aligned with design prototype.
 *
 * After the list, one POST /public/catalog-card-stats with page slugs aligns
 * rating/website with the same DB logic as the list (single query, no N+1 GET).
 */
import { get, post } from "../utils/api";

/** Coerce API number fields (strings, DECIMAL-as-string from some gateways) */
function coercePositiveRating(v) {
  if (v == null || v === "") return null;
  const n = Number(v);
  if (!Number.isFinite(n) || n <= 0) return null;
  return n;
}

function coerceReviewCount(v) {
  if (v == null || v === "") return null;
  const n = Math.round(Number(v));
  if (!Number.isFinite(n) || n < 0) return null;
  return n;
}

function strOrNull(v) {
  if (v == null || v === "") return null;
  const s = String(v).trim();
  return s || null;
}

/** Google Places price_level 0–4 */
function coercePriceLevel(v) {
  if (v == null || v === "") return null;
  const n = Math.round(Number(v));
  if (!Number.isFinite(n) || n < 0 || n > 4) return null;
  return n;
}

/** Unify catalog item shape — same fields as public profile (`/public/businesses/:slug`) */
function normalizeCatalogBusiness(b) {
  if (!b || typeof b !== "object") return b;
  const googleRating =
    coercePositiveRating(b.googleRating) ??
    coercePositiveRating(b.google_rating) ??
    coercePositiveRating(b.rating) ??
    null;
  const googleRatingCount =
    coerceReviewCount(b.googleRatingCount) ??
    coerceReviewCount(b.google_rating_count) ??
    coerceReviewCount(b.user_rating_count) ??
    coerceReviewCount(b.userRatingCount) ??
    coerceReviewCount(b.reviews_count) ??
    null;
  let website =
    strOrNull(b.website) ?? strOrNull(b.websiteUrl) ?? strOrNull(b.website_url) ?? null;
  if (website && ["null", "undefined", "n/a", "none"].includes(website.toLowerCase())) website = null;
  const ownershipStatus = b.ownershipStatus ?? b.ownership_status ?? null;
  const priceLevel =
    coercePriceLevel(b.priceLevel) ??
    coercePriceLevel(b.price_level) ??
    null;
  let workingHours = b.workingHours ?? b.working_hours ?? null;
  if (typeof workingHours === "string") {
    try {
      workingHours = JSON.parse(workingHours);
    } catch {
      workingHours = null;
    }
  }
  return {
    ...b,
    googleRating,
    googleRatingCount: googleRatingCount ?? 0,
    website,
    ownershipStatus,
    priceLevel,
    workingHours,
  };
}

/** Merge batch stats (same server logic as list card mapping) — 1 request per Explore load */
async function mergeCatalogStatsBatch(rows) {
  if (!Array.isArray(rows) || !rows.length) return;
  const slugs = [...new Set(rows.map((b) => String(b?.slug || "").trim()).filter(Boolean))];
  if (!slugs.length) return;
  // Always merge: list row can omit stats while batch query + merge matches profile; skipping when
  // "website present" left ratings empty if the list endpoint dropped googleRating.
  try {
    const batch = await post("/public/catalog-card-stats", { slugs });
    const items = batch?.data;
    if (!Array.isArray(items) || !items.length) return;
    const bySlug = new Map(items.map((x) => [x.slug, x]));
    for (const b of rows) {
      const x = bySlug.get(b.slug);
      if (!x) continue;
      if (x.googleRating != null && Number(x.googleRating) > 0) {
        b.googleRating = Number(x.googleRating);
        if (x.googleRatingCount != null) b.googleRatingCount = Number(x.googleRatingCount) || 0;
      } else if (x.googleRatingCount != null && Number(x.googleRatingCount) > 0) {
        b.googleRatingCount = Number(x.googleRatingCount);
      }
      if (strOrNull(x.website)) b.website = strOrNull(x.website);
      const pl = coercePriceLevel(x.priceLevel ?? x.price_level);
      if (pl != null) b.priceLevel = pl;
      const os = x.ownershipStatus ?? x.ownership_status;
      if (os !== undefined) b.ownershipStatus = os == null || os === "" ? null : String(os).trim() || null;
    }
  } catch (err) {
    console.warn("[catalogService] catalog-stats batch failed:", err.message);
  }
}

export async function fetchBusinesses({ q, category, city, page = 1, limit = 12 } = {}) {
  try {
    const params = new URLSearchParams();
    if (q) params.set("q", q);
    if (category) params.set("category", category);
    if (city) params.set("city", city);
    params.set("page", String(page));
    params.set("limit", String(limit));
    const query = params.toString();
    const res = await get(`/public/businesses${query ? `?${query}` : ""}`);
    if (res && Array.isArray(res.data)) {
      res.data = res.data.map(normalizeCatalogBusiness);
      await mergeCatalogStatsBatch(res.data);
      res.data = res.data.map(normalizeCatalogBusiness);
    }
    return res;
  } catch (err) {
    console.warn("[catalogService] fetchBusinesses failed:", err.message);
    return { data: [], meta: { page, limit, total: 0 } };
  }
}

/**
 * Fetch single business by slug or id (for profile page)
 */
export async function fetchBusinessBySlugOrId(slugOrId) {
  try {
    // Always request preview=1 when user is authenticated.
    // Backend returns unpublished draft only if the JWT owner matches the business owner;
    // for everyone else, the preview flag is silently ignored.
    const hasToken = typeof window !== "undefined" && !!localStorage.getItem("auth_token");
    const url = hasToken
      ? `/public/businesses/${encodeURIComponent(slugOrId)}?preview=1`
      : `/public/businesses/${encodeURIComponent(slugOrId)}`;
    const res = await get(url);
    return res?.data ?? null;
  } catch (err) {
    console.warn("[catalogService] fetchBusinessBySlugOrId failed:", err.message);
    return null;
  }
}

export async function fetchCategories() {
  try {
    const res = await get("/categories");
    return res?.categories ?? [];
  } catch {
    return [];
  }
}
